import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import { alertsApi } from '@api/alerts'
import { fetchAlertRules } from '@store/slices/alertRulesSlice'
import { AlertRule } from '@types'

interface DefaultAlertRule extends AlertRule {
  selected: boolean
}

/**
 * AlertRulesDefault Page Component
 * 
 * Allows users to select and copy predefined alert rules from Smart Nyuki's default templates.
 * Features:
 * - Browse default alert rule templates
 * - Select multiple rules to copy
 * - Preview rule conditions and thresholds
 * - Batch copy to user's alert rules
 * 
 * @component
 */
const AlertRulesDefault: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { alertRules } = useAppSelector((state) => state.alertRules)

  const [defaultRules, setDefaultRules] = useState<DefaultAlertRule[]>([])
  const [loading, setLoading] = useState(true)
  const [copying, setCopying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const selectedCount = defaultRules.filter(rule => rule.selected).length

  useEffect(() => {
    loadDefaultAlertRules()
  }, [])

  const loadDefaultAlertRules = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await alertsApi.getDefaultAlertRules()
      const rulesWithSelection = response.data.data.map((rule: AlertRule) => ({
        ...rule,
        selected: false,
      }))
      setDefaultRules(rulesWithSelection)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load default alert rules')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectAll = (checked: boolean) => {
    setDefaultRules(rules => rules.map(rule => ({ ...rule, selected: checked })))
  }

  const handleSelectRule = (index: number, checked: boolean) => {
    setDefaultRules(rules =>
      rules.map((rule, i) =>
        i === index ? { ...rule, selected: checked } : rule
      )
    )
  }

  const copySelectedRules = async () => {
    const selectedRules = defaultRules.filter(rule => rule.selected)
    if (selectedRules.length === 0) return

    setCopying(true)
    setError(null)
    setSuccessMessage(null)

    try {
      await alertsApi.createDefaultAlertRules()
      setSuccessMessage(`Successfully copied ${selectedRules.length} alert rule${selectedRules.length > 1 ? 's' : ''}`)

      // Refresh the user's alert rules
      dispatch(fetchAlertRules())

      // Reset selections
      setDefaultRules(rules => rules.map(rule => ({ ...rule, selected: false })))
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to copy alert rules')
    } finally {
      setCopying(false)
    }
  }

  const formatAlertRuleDescription = (rule: AlertRule) => {
    const measurement = rule.measurement_type_name || 'any sensor'
    const comparison = rule.comparison_operator || '>'
    const threshold = rule.threshold_value ?? 0
    const calculation = rule.calculation || 'most recent value'

    return `Alert when ${measurement} ${comparison} ${threshold} (${calculation})`
  }

  const formatCalculationTime = (minutes?: number) => {
    if (!minutes || minutes === 0) return 'Instant'
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <Container sx={{ py: 3 }}>
      {/* Toolbar */}
      <Toolbar sx={{ px: 0, mb: 2, justifyContent: 'space-between' }}>
        <IconButton onClick={() => navigate('/alertrules')}>
          <BackIcon />
        </IconButton>
        <Button
          variant="contained"
          startIcon={copying ? <CircularProgress size={20} /> : <CopyIcon />}
          onClick={copySelectedRules}
          disabled={selectedCount === 0 || copying}
        >
          Copy {selectedCount} Rule{selectedCount !== 1 ? 's' : ''}
        </Button>
      </Toolbar>

      {/* Header */}
      <Typography variant="h4" component="h1" gutterBottom>
        Default Alert Rules
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Select from our curated collection of alert rules designed by beekeeping experts.
        These templates cover common monitoring scenarios and can be customized after copying.
      </Typography>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Select All */}
      {defaultRules.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCount === defaultRules.length}
                  indeterminate={selectedCount > 0 && selectedCount < defaultRules.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              }
              label={`Select All (${selectedCount}/${defaultRules.length} selected)`}
            />
          </CardContent>
        </Card>
      )}

      {/* Default Rules List */}
      {defaultRules.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No default alert rules available
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Check back later for new template alert rules.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {defaultRules.map((rule, index) => (
            <Grid item xs={12} key={rule.id}>
              <Card
                sx={{
                  border: rule.selected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                  borderColor: rule.selected ? 'primary.main' : 'divider',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Checkbox
                      checked={rule.selected}
                      onChange={(e) => handleSelectRule(index, e.target.checked)}
                      sx={{ mt: -1 }}
                    />

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" component="h3">
                          {rule.name}
                        </Typography>
                        {rule.active && (
                          <Chip
                            icon={<CheckIcon />}
                            label="Active"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {rule.description && (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          {rule.description}
                        </Typography>
                      )}

                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {formatAlertRuleDescription(rule)}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {rule.measurement_type_name && (
                          <Chip
                            label={rule.measurement_type_name}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        <Chip
                          label={`Calculation: ${formatCalculationTime(rule.calculation_minutes)}`}
                          size="small"
                          variant="outlined"
                        />
                        {rule.alert_via_email && (
                          <Chip
                            label="Email alerts"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                        {rule.alert_via_sms && (
                          <Chip
                            label="SMS alerts"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Info Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="overline" display="block" gutterBottom>
            About Default Rules
          </Typography>
          <Typography variant="body2" color="textSecondary">
            These alert rules are based on best practices from experienced beekeepers and researchers.
            After copying, you can customize thresholds, add email notifications, and modify conditions
            to match your specific monitoring needs.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  )
}

export default AlertRulesDefault
